import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import toast from "react-hot-toast";
import {
  Plus,
  ArrowRightLeft,
  Eye,
  Check,
  X,
  RefreshCw,
  Building2,
  Table2,
} from "lucide-react";
import {
  BodyLargeBold,
  BodyNormalBold,
  BodySmallMedium,
  BodySmallRegular,
} from "../components/ui/Typography";
import { fillTransactionService } from "../api/services/fillTransaction";
import { gameTableService } from "../api/services/gameTables/gameTableService";
import type {
  FillTransaction as FillTransactionCreate,
  FillTransaction as FillTransactionType,
  TableTransferCreate,
} from "../api/services/fillTransaction/type";
import type { GameTable as GameTableApi } from "../api/services/gameTables/type";

interface FillTransactionFormData
  extends Omit<FillTransactionCreate, "amount" | "coin_quantity"> {
  coin_quantities: { [key: string]: number };
}

interface TableTransferFormData
  extends Omit<TableTransferCreate, "amount" | "coin_quantity"> {
  coin_quantities: { [key: string]: number };
}

const DEFAULT_DENOMINATIONS = [
  { value: "500", label: "$500", color: "text-green-500" },
  { value: "1000", label: "$1,000", color: "text-blue-500" },
];

const SHIFTS = Array.from({ length: 24 }, (_, i) => {
  const hour =
    i === 0 ? "12AM" : i < 12 ? `${i}AM` : i === 12 ? "12PM" : `${i - 12}PM`;
  return { value: hour, label: hour };
});

export const FillTransactionPage: React.FC = () => {
  const [transactions, setTransactions] = useState<FillTransactionType[]>([]);
  const [tables, setTables] = useState<GameTableApi[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [tablesLoaded, setTablesLoaded] = useState(false);
  const [customDenominations, setCustomDenominations] = useState<
    Array<{ value: string; label: string; color: string }>
  >([]);
  const [newDenomValue, setNewDenomValue] = useState("");
  const [selectedTransaction, setSelectedTransaction] =
    useState<FillTransactionType | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    transaction_type: "",
    table_id: "",
    shift: "",
    date_from: "",
    date_to: "",
  });
  // const hasFetchedTables = useRef(false);
  // const hasFetchedInitialTransactions = useRef(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FillTransactionFormData>({
    defaultValues: {
      coin_quantities: {},
    },
    resolver: (values) => {
      // Ensure coin_quantities is always an object
      if (Array.isArray(values.coin_quantities)) {
        values.coin_quantities = {};
      } else if (
        typeof values.coin_quantities !== "object" ||
        values.coin_quantities === null
      ) {
        values.coin_quantities = {};
      }
      return {
        values,
        errors: {},
      };
    },
  });

  const {
    register: registerTransfer,
    handleSubmit: handleSubmitTransfer,
    reset: resetTransfer,
    formState: { errors: errorsTransfer, isSubmitting: isSubmittingTransfer },
  } = useForm<TableTransferFormData>({
    defaultValues: {
      coin_quantities: {},
    },
    resolver: (values) => {
      // Ensure coin_quantities is always an object
      if (Array.isArray(values.coin_quantities)) {
        values.coin_quantities = {};
      } else if (
        typeof values.coin_quantities !== "object" ||
        values.coin_quantities === null
      ) {
        values.coin_quantities = {};
      }
      return {
        values,
        errors: {},
      };
    },
  });

  const sourceType = watch("source_type");
  const destinationType = watch("destination_type");

  // Fetch tables only once on mount
  useEffect(() => {
    if (!tablesLoaded) {
      fetchTables();
    }
  }, [tablesLoaded]);

  // Fetch transactions when filters change
  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response =
        await fillTransactionService.getFillTransactions(filters);
      setTransactions(response.items || []);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  const fetchTables = async () => {
    try {
      const response = await gameTableService.getGameTables();
      setTables(response.gameTables);
      setTablesLoaded(true);
    } catch (error) {
      console.error("Error fetching tables:", error);
      toast.error("Failed to fetch tables");
      setTablesLoaded(true); // Set as loaded even on error to prevent infinite retries
    }
  };

  const onSubmitFillTransaction: SubmitHandler<
    FillTransactionFormData
  > = async (data) => {
    try {
      // Create a clean coin_quantities object with only valid values
      const cleanCoinQuantities: Record<string, number> = {};
      let totalAmount = 0;

      // Process coin quantities if they exist
      if (data.coin_quantities && typeof data.coin_quantities === "object") {
        Object.entries(data.coin_quantities).forEach(([denom, qty]) => {
          const quantity = Number(qty);
          // Only include if qty is a valid number and greater than 0
          if (!isNaN(quantity) && quantity > 0) {
            cleanCoinQuantities[denom] = quantity;
            totalAmount += parseInt(denom) * quantity;
          }
        });
      }

      if (totalAmount === 0) {
        toast.error("Please enter at least one coin denomination");
        return;
      }

      // Create a new object without coin_quantities
      // Using destructuring with void to explicitly ignore the unused variable
      const { coin_quantities, ...restData } = data;
      void coin_quantities; // Explicitly mark as unused
      const payload: FillTransactionCreate = {
        ...restData,
        amount: totalAmount,
        coin_quantity: cleanCoinQuantities,
      };

      await fillTransactionService.createFillTransaction(payload);
      toast.success("Fill transaction created successfully");
      setShowCreateModal(false);
      reset();
      fetchTransactions();
    } catch (error) {
      console.error("Error creating transaction:", error);
      toast.error("Failed to create transaction");
    }
  };

  const onSubmitTableTransfer: SubmitHandler<TableTransferFormData> = async (
    data,
  ) => {
    try {
      // Create a clean coin_quantities object with only valid values
      const cleanCoinQuantities: Record<string, number> = {};
      let totalAmount = 0;

      // Process coin quantities if they exist
      if (data.coin_quantities && typeof data.coin_quantities === "object") {
        Object.entries(data.coin_quantities).forEach(([denom, qty]) => {
          const quantity = Number(qty);
          // Only include if qty is a valid number and greater than 0
          if (!isNaN(quantity) && quantity > 0) {
            cleanCoinQuantities[denom] = quantity;
            totalAmount += parseInt(denom) * quantity;
          }
        });
      }

      if (totalAmount === 0) {
        toast.error("Please enter at least one coin denomination");
        return;
      }

      if (data.source_table_id === data.destination_table_id) {
        toast.error("Source and destination tables cannot be the same");
        return;
      }

      // Create a new object without coin_quantities
      // Using destructuring with void to explicitly ignore the unused variable
      const { coin_quantities, ...restData } = data;
      void coin_quantities; // Explicitly mark as unused
      const payload: TableTransferCreate = {
        ...restData,
        amount: totalAmount,
        coin_quantity: cleanCoinQuantities,
      };

      await fillTransactionService.createTableTransfer(payload);
      toast.success("Table transfer created successfully");
      setShowTransferModal(false);
      resetTransfer();
      fetchTransactions();
    } catch (error) {
      console.error("Error creating table transfer:", error);
      toast.error("Failed to create table transfer");
    }
  };

  const handleApprove = async (transactionId: string) => {
    try {
      await fillTransactionService.approveFillTransaction(transactionId);
      toast.success("Transaction approved");
      fetchTransactions();
    } catch (error) {
      console.error("Error approving transaction:", error);
      toast.error("Failed to approve transaction");
    }
  };

  const handleReject = async (transactionId: string) => {
    const reason = prompt("Please enter rejection reason:");
    if (!reason) return;

    try {
      await fillTransactionService.rejectFillTransaction(transactionId, reason);
      toast.success("Transaction rejected");
      fetchTransactions();
    } catch (error) {
      console.error("Error rejecting transaction:", error);
      toast.error("Failed to reject transaction");
    }
  };

  const handleComplete = async (transactionId: string) => {
    try {
      await fillTransactionService.completeFillTransaction(transactionId);
      toast.success("Transaction completed");
      fetchTransactions();
    } catch (error) {
      console.error("Error completing transaction:", error);
      toast.error("Failed to complete transaction");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "APPROVED":
        return "bg-blue-100 text-blue-800";
      case "COMPLETED":
        return "bg-green-100 text-green-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      case "CANCELLED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTransactionTypeColor = (type: string) => {
    return type === "FILL_IN"
      ? "bg-green-100 text-green-800"
      : "bg-orange-100 text-orange-800";
  };

  return (
    <div className="p-6 bg-background min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <BodyLargeBold className="text-white mb-2">
          Fill Transactions
        </BodyLargeBold>
        <BodySmallRegular className="text-gray-400">
          Manage chip movements between tables and cashier
        </BodySmallRegular>
      </div>

      {/* Stats Cards */}
      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <BodySmallRegular className="text-gray-400">
                Total Fill In
              </BodySmallRegular>
              <BodyNormalBold className="text-green-400 mt-1">
                $125,000
              </BodyNormalBold>
            </div>
            <Upload className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <BodySmallRegular className="text-gray-400">
                Total Fill Out
              </BodySmallRegular>
              <BodyNormalBold className="text-orange-400 mt-1">
                $85,000
              </BodyNormalBold>
            </div>
            <Download className="w-8 h-8 text-orange-400" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <BodySmallRegular className="text-gray-400">
                Pending
              </BodySmallRegular>
              <BodyNormalBold className="text-yellow-400 mt-1">
                5
              </BodyNormalBold>
            </div>
            <RefreshCw className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <BodySmallRegular className="text-gray-400">
                {"Today's Count"}
              </BodySmallRegular>
              <BodyNormalBold className="text-primary mt-1">23</BodyNormalBold>
            </div>
            <DollarSign className="w-8 h-8 text-primary" />
          </div>
        </div>
      </div> */}

      {/* Filters and Actions */}
      <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-3">
            <select
              className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="COMPLETED">Completed</option>
              <option value="REJECTED">Rejected</option>
            </select>

            <select
              className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
              value={filters.transaction_type}
              onChange={(e) =>
                setFilters({ ...filters, transaction_type: e.target.value })
              }
            >
              <option value="">All Types</option>
              <option value="FILL_IN">Fill In</option>
              <option value="FILL_OUT">Fill Out</option>
            </select>

            <select
              className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
              value={filters.shift}
              onChange={(e) =>
                setFilters({ ...filters, shift: e.target.value })
              }
            >
              <option value="">All Shifts</option>
              {SHIFTS.map((shift) => (
                <option key={shift.value} value={shift.value}>
                  {shift.label}
                </option>
              ))}
            </select>

            <button
              onClick={fetchTransactions}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Fill
            </button>

            <button
              onClick={() => setShowTransferModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <ArrowRightLeft className="w-4 h-4" />
              Table Transfer
            </button>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    ID
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Type
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Amount
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Source
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Destination
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Status
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Shift
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Created By
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Date
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Actions
                  </BodySmallMedium>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center">
                    <BodySmallRegular className="text-gray-400">
                      Loading...
                    </BodySmallRegular>
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center">
                    <BodySmallRegular className="text-gray-400">
                      No transactions found
                    </BodySmallRegular>
                  </td>
                </tr>
              ) : (
                transactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className="border-b border-gray-700 hover:bg-gray-700/50"
                  >
                    <td className="px-4 py-3">
                      <BodySmallRegular className="text-white">
                        #{transaction.id.slice(0, 8)}
                      </BodySmallRegular>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getTransactionTypeColor(transaction.transaction_type)}`}
                      >
                        {transaction.transaction_type.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <BodySmallMedium className="text-yellow-400">
                        ${transaction.amount.toLocaleString()}
                      </BodySmallMedium>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {transaction.source_type === "CASH" ? (
                          <>
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <BodySmallRegular className="text-white">
                              Cash
                            </BodySmallRegular>
                          </>
                        ) : (
                          <>
                            <Table2 className="w-4 h-4 text-gray-400" />
                            <BodySmallRegular className="text-white">
                              {transaction.source_table?.name || "Table"}
                            </BodySmallRegular>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {transaction.destination_type === "CASH" ? (
                          <>
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <BodySmallRegular className="text-white">
                              Cash
                            </BodySmallRegular>
                          </>
                        ) : (
                          <>
                            <Table2 className="w-4 h-4 text-gray-400" />
                            <BodySmallRegular className="text-white">
                              {transaction.destination_table?.name || "Table"}
                            </BodySmallRegular>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}
                      >
                        {transaction.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <BodySmallRegular className="text-white">
                        {transaction.shift}
                      </BodySmallRegular>
                    </td>
                    <td className="px-4 py-3">
                      <BodySmallRegular className="text-white">
                        {transaction.created_by?.username || "Unknown"}
                      </BodySmallRegular>
                    </td>
                    <td className="px-4 py-3">
                      <BodySmallRegular className="text-gray-400">
                        {new Date(
                          transaction.transaction_date,
                        ).toLocaleDateString()}
                      </BodySmallRegular>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            setShowDetailsModal(true);
                          }}
                          className="p-1 hover:bg-gray-600 rounded transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 text-gray-400" />
                        </button>

                        {transaction.status === "PENDING" && (
                          <>
                            <button
                              onClick={() => handleApprove(transaction.id)}
                              className="p-1 hover:bg-green-600/20 rounded transition-colors"
                              title="Approve"
                            >
                              <Check className="w-4 h-4 text-green-400" />
                            </button>
                            <button
                              onClick={() => handleReject(transaction.id)}
                              className="p-1 hover:bg-red-600/20 rounded transition-colors"
                              title="Reject"
                            >
                              <X className="w-4 h-4 text-red-400" />
                            </button>
                          </>
                        )}

                        {transaction.status === "APPROVED" && (
                          <button
                            onClick={() => handleComplete(transaction.id)}
                            className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                          >
                            Complete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Fill Transaction Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700">
              <BodyNormalBold className="text-white">
                Create Fill Transaction
              </BodyNormalBold>
            </div>

            <form
              onSubmit={handleSubmit(onSubmitFillTransaction)}
              className="p-6"
            >
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2">
                    <BodySmallMedium className="text-gray-400">
                      Transaction Type
                    </BodySmallMedium>
                  </label>
                  <select
                    {...register("transaction_type", {
                      required: "Transaction type is required",
                    })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
                  >
                    <option value="">Select Type</option>
                    <option value="FILL_IN">Fill In</option>
                    <option value="FILL_OUT">Fill Out</option>
                  </select>
                  {errors.transaction_type && (
                    <BodySmallRegular className="text-red-400 mt-1">
                      {errors.transaction_type.message}
                    </BodySmallRegular>
                  )}
                </div>

                <div>
                  <label className="block mb-2">
                    <BodySmallMedium className="text-gray-400">
                      Shift
                    </BodySmallMedium>
                  </label>
                  <select
                    {...register("shift", { required: "Shift is required" })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
                  >
                    <option value="">Select Shift</option>
                    {SHIFTS.map((shift) => (
                      <option key={shift.value} value={shift.value}>
                        {shift.label}
                      </option>
                    ))}
                  </select>
                  {errors.shift && (
                    <BodySmallRegular className="text-red-400 mt-1">
                      {errors.shift.message}
                    </BodySmallRegular>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2">
                    <BodySmallMedium className="text-gray-400">
                      Source Type
                    </BodySmallMedium>
                  </label>
                  <select
                    {...register("source_type", {
                      required: "Source type is required",
                    })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
                  >
                    <option value="">Select Source</option>
                    <option value="CASH">Cash</option>
                    <option value="TABLE">Table</option>
                  </select>
                </div>

                {sourceType === "TABLE" && (
                  <div>
                    <label className="block mb-2">
                      <BodySmallMedium className="text-gray-400">
                        Source Table
                      </BodySmallMedium>
                    </label>
                    <select
                      {...register("source_table_id")}
                      className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
                    >
                      <option value="">Select Table</option>
                      {tables.map((table) => (
                        <option key={table.id} value={table.id}>
                          {table.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block mb-2">
                    <BodySmallMedium className="text-gray-400">
                      Destination Type
                    </BodySmallMedium>
                  </label>
                  <select
                    {...register("destination_type", {
                      required: "Destination type is required",
                    })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
                  >
                    <option value="">Select Destination</option>
                    <option value="CASH">Cash</option>
                    <option value="TABLE">Table</option>
                  </select>
                </div>

                {destinationType === "TABLE" && (
                  <div>
                    <label className="block mb-2">
                      <BodySmallMedium className="text-gray-400">
                        Destination Table
                      </BodySmallMedium>
                    </label>
                    <select
                      {...register("destination_table_id")}
                      className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
                    >
                      <option value="">Select Table</option>
                      {tables.map((table) => (
                        <option key={table.id} value={table.id}>
                          {table.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Coin Breakdown */}
              <div className="mb-6 p-4 bg-gray-700/50 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <BodySmallMedium className="text-white">
                    Coin Breakdown
                  </BodySmallMedium>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="1"
                      value={newDenomValue}
                      onChange={(e) => setNewDenomValue(e.target.value)}
                      placeholder="Add custom amount"
                      className="w-32 bg-gray-600 text-white px-3 py-1 rounded border border-gray-500 focus:outline-none focus:border-primary"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          newDenomValue &&
                          !isNaN(Number(newDenomValue)) &&
                          Number(newDenomValue) > 0
                        ) {
                          const value = newDenomValue;
                          const label = `$${Number(value).toLocaleString()}`;
                          if (
                            ![
                              ...DEFAULT_DENOMINATIONS,
                              ...customDenominations,
                            ].some((d) => d.value === value)
                          ) {
                            setCustomDenominations([
                              ...customDenominations,
                              {
                                value,
                                label,
                                color: "text-purple-500",
                              },
                            ]);
                          }
                          setNewDenomValue("");
                        }
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                    >
                      Add
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {[...DEFAULT_DENOMINATIONS, ...customDenominations].map(
                    (denom) => (
                      <div key={denom.value} className="relative">
                        <label className="block mb-1">
                          <BodySmallRegular className={denom.color}>
                            {denom.label}
                          </BodySmallRegular>
                        </label>
                        <input
                          type="number"
                          min="0"
                          defaultValue={0}
                          {...register(`coin_quantities.${denom.value}`, {
                            valueAsNumber: true,
                            validate: (value) => {
                              const num = Number(value);
                              return !isNaN(num) && num >= 0;
                            },
                          })}
                          className="w-full bg-gray-600 text-white px-3 py-1 rounded border border-gray-500 focus:outline-none focus:border-primary pr-8"
                        />
                        {!DEFAULT_DENOMINATIONS.some(
                          (d) => d.value === denom.value,
                        ) && (
                          <button
                            type="button"
                            onClick={() => {
                              setCustomDenominations(
                                customDenominations.filter(
                                  (d) => d.value !== denom.value,
                                ),
                              );
                              // Reset the input value when removed
                              const formValues = getValues();
                              if (
                                formValues.coin_quantities?.[denom.value] !==
                                undefined
                              ) {
                                setValue(`coin_quantities.${denom.value}`, 0);
                              }
                            }}
                            className="absolute right-2 top-7 text-red-500 hover:text-red-400"
                            title="Remove denomination"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block mb-2">
                  <BodySmallMedium className="text-gray-400">
                    Notes
                  </BodySmallMedium>
                </label>
                <textarea
                  {...register("notes")}
                  rows={3}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
                  placeholder="Enter any additional notes..."
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    reset();
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Creating..." : "Create Transaction"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700">
              <BodyNormalBold className="text-white">
                Table to Table Transfer
              </BodyNormalBold>
            </div>

            <form
              onSubmit={handleSubmitTransfer(onSubmitTableTransfer)}
              className="p-6"
            >
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2">
                    <BodySmallMedium className="text-gray-400">
                      Source Table (From)
                    </BodySmallMedium>
                  </label>
                  <select
                    {...registerTransfer("source_table_id", {
                      required: "Source table is required",
                    })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
                  >
                    <option value="">Select Source Table</option>
                    {tables.map((table) => (
                      <option key={table.id} value={table.id}>
                        {table.name}
                      </option>
                    ))}
                  </select>
                  {errorsTransfer.source_table_id && (
                    <BodySmallRegular className="text-red-400 mt-1">
                      {errorsTransfer.source_table_id.message}
                    </BodySmallRegular>
                  )}
                </div>

                <div>
                  <label className="block mb-2">
                    <BodySmallMedium className="text-gray-400">
                      Destination Table (To)
                    </BodySmallMedium>
                  </label>
                  <select
                    {...registerTransfer("destination_table_id", {
                      required: "Destination table is required",
                    })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
                  >
                    <option value="">Select Destination Table</option>
                    {tables.map((table) => (
                      <option key={table.id} value={table.id}>
                        {table.name}
                      </option>
                    ))}
                  </select>
                  {errorsTransfer.destination_table_id && (
                    <BodySmallRegular className="text-red-400 mt-1">
                      {errorsTransfer.destination_table_id.message}
                    </BodySmallRegular>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block mb-2">
                  <BodySmallMedium className="text-gray-400">
                    Shift
                  </BodySmallMedium>
                </label>
                <select
                  {...registerTransfer("shift", {
                    required: "Shift is required",
                  })}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
                >
                  <option value="">Select Shift</option>
                  {SHIFTS.map((shift) => (
                    <option key={shift.value} value={shift.value}>
                      {shift.label}
                    </option>
                  ))}
                </select>
                {errorsTransfer.shift && (
                  <BodySmallRegular className="text-red-400 mt-1">
                    {errorsTransfer.shift.message}
                  </BodySmallRegular>
                )}
              </div>

              {/* Coin Breakdown */}
              <div className="mb-6 p-4 bg-gray-700/50 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <BodySmallMedium className="text-white">
                    Coin Breakdown
                  </BodySmallMedium>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="1"
                      value={newDenomValue}
                      onChange={(e) => setNewDenomValue(e.target.value)}
                      placeholder="Add custom amount"
                      className="w-32 bg-gray-600 text-white px-3 py-1 rounded border border-gray-500 focus:outline-none focus:border-primary"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          newDenomValue &&
                          !isNaN(Number(newDenomValue)) &&
                          Number(newDenomValue) > 0
                        ) {
                          const value = newDenomValue;
                          const label = `$${Number(value).toLocaleString()}`;
                          if (
                            ![
                              ...DEFAULT_DENOMINATIONS,
                              ...customDenominations,
                            ].some((d) => d.value === value)
                          ) {
                            setCustomDenominations([
                              ...customDenominations,
                              {
                                value,
                                label,
                                color: "text-purple-500",
                              },
                            ]);
                          }
                          setNewDenomValue("");
                        }
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                    >
                      Add
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {[...DEFAULT_DENOMINATIONS, ...customDenominations].map(
                    (denom: {
                      value: string;
                      label: string;
                      color: string;
                    }) => (
                      <div key={denom.value} className="relative">
                        <label className="block mb-1">
                          <BodySmallRegular className={denom.color}>
                            {denom.label}
                          </BodySmallRegular>
                        </label>
                        <input
                          type="number"
                          min="0"
                          defaultValue={0}
                          {...registerTransfer(
                            `coin_quantities.${denom.value}`,
                          )}
                          className="w-full bg-gray-600 text-white px-3 py-1 rounded border border-gray-500 focus:outline-none focus:border-primary pr-8"
                        />
                        {!DEFAULT_DENOMINATIONS.some(
                          (d) => d.value === denom.value,
                        ) && (
                          <button
                            type="button"
                            onClick={() => {
                              setCustomDenominations(
                                customDenominations.filter(
                                  (d) => d.value !== denom.value,
                                ),
                              );
                              // Reset the input value when removed
                              const formValues = getValues();
                              if (
                                formValues.coin_quantities?.[denom.value] !==
                                undefined
                              ) {
                                setValue(`coin_quantities.${denom.value}`, 0);
                              }
                            }}
                            className="absolute right-2 top-7 text-red-500 hover:text-red-400"
                            title="Remove denomination"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block mb-2">
                  <BodySmallMedium className="text-gray-400">
                    Notes
                  </BodySmallMedium>
                </label>
                <textarea
                  {...registerTransfer("notes")}
                  rows={3}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-primary"
                  placeholder="Enter transfer notes..."
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowTransferModal(false);
                    resetTransfer();
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingTransfer}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                  {isSubmittingTransfer ? "Creating..." : "Create Transfer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transaction Details Modal */}
      {showDetailsModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700">
              <BodyNormalBold className="text-white">
                Transaction Details
              </BodyNormalBold>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <BodySmallRegular className="text-gray-400 mb-1">
                    Transaction ID
                  </BodySmallRegular>
                  <BodySmallMedium className="text-white">
                    {selectedTransaction.id}
                  </BodySmallMedium>
                </div>
                <div>
                  <BodySmallRegular className="text-gray-400 mb-1">
                    Type
                  </BodySmallRegular>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getTransactionTypeColor(selectedTransaction.transaction_type)}`}
                  >
                    {selectedTransaction.transaction_type.replace("_", " ")}
                  </span>
                </div>
                <div>
                  <BodySmallRegular className="text-gray-400 mb-1">
                    Status
                  </BodySmallRegular>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedTransaction.status)}`}
                  >
                    {selectedTransaction.status}
                  </span>
                </div>
                <div>
                  <BodySmallRegular className="text-gray-400 mb-1">
                    Amount
                  </BodySmallRegular>
                  <BodyNormalBold className="text-yellow-400">
                    ${selectedTransaction.amount.toLocaleString()}
                  </BodyNormalBold>
                </div>
              </div>

              {selectedTransaction.coin_quantity && (
                <div className="mb-4">
                  <BodySmallRegular className="text-gray-400 mb-2">
                    Coin Breakdown
                  </BodySmallRegular>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(selectedTransaction.coin_quantity).map(
                      ([denom, qty]) => (
                        <span
                          key={denom}
                          className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm"
                        >
                          ${denom} × {qty}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <BodySmallRegular className="text-gray-400 mb-1">
                    Source
                  </BodySmallRegular>
                  <BodySmallMedium className="text-white">
                    {selectedTransaction.source_type === "CASH"
                      ? "Cash"
                      : selectedTransaction.source_table?.name || "Table"}
                  </BodySmallMedium>
                </div>
                <div>
                  <BodySmallRegular className="text-gray-400 mb-1">
                    Destination
                  </BodySmallRegular>
                  <BodySmallMedium className="text-white">
                    {selectedTransaction.destination_type === "CASH"
                      ? "Cash"
                      : selectedTransaction.destination_table?.name || "Table"}
                  </BodySmallMedium>
                </div>
                <div>
                  <BodySmallRegular className="text-gray-400 mb-1">
                    Shift
                  </BodySmallRegular>
                  <BodySmallMedium className="text-white">
                    {selectedTransaction.shift}
                  </BodySmallMedium>
                </div>
                <div>
                  <BodySmallRegular className="text-gray-400 mb-1">
                    Created By
                  </BodySmallRegular>
                  <BodySmallMedium className="text-white">
                    {selectedTransaction.created_by?.first_name || ""}{" "}
                    {selectedTransaction.created_by?.last_name || ""}
                  </BodySmallMedium>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <BodySmallRegular className="text-gray-400 mb-1">
                    Transaction Date
                  </BodySmallRegular>
                  <BodySmallMedium className="text-white">
                    {new Date(
                      selectedTransaction.transaction_date,
                    ).toLocaleString()}
                  </BodySmallMedium>
                </div>
                {selectedTransaction.approved_by && (
                  <div>
                    <BodySmallRegular className="text-gray-400 mb-1">
                      Approved By
                    </BodySmallRegular>
                    <BodySmallMedium className="text-white">
                      {selectedTransaction.approved_by.username}
                    </BodySmallMedium>
                  </div>
                )}
              </div>

              {selectedTransaction.notes && (
                <div className="mb-4">
                  <BodySmallRegular className="text-gray-400 mb-1">
                    Notes
                  </BodySmallRegular>
                  <BodySmallMedium className="text-white">
                    {selectedTransaction.notes}
                  </BodySmallMedium>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedTransaction(null);
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const FillTransaction = FillTransactionPage;
